import { workspaceTextFor, type WorkspaceText } from './interface-copy';
import { useWorkspaceText } from './interface-copy';
import type { WorkspaceState } from './context';
import { WorkspaceIcon, type WorkspaceIconName } from './icons';
import { selectedSkillSnapshot } from './skill-context';
import { isNewAppCanvas } from './apps/new-app';
import { isNewAgentCanvas, makingNewAgent, withoutNewAgent } from './agents/new-agent';
import { useNamespaceCopy } from '@/i18n/ns';
import appsNavCopy from '@/i18n/ns/apps-nav';
import buildWatchCopy from '@/i18n/ns/build-watch';
import { appSelectionLabel } from './apps/preview/live-build-model';
import storefrontCopy from '@/i18n/ns/storefront';
import { hasProjectChips, ProjectContextChips } from './projects/place';

function ReferenceChip({
  label,
  icon,
  disabled,
  onRemove,
}: {
  label: string;
  icon: WorkspaceIconName;
  disabled: boolean;
  onRemove?: () => void;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const content = (
    <>
      <WorkspaceIcon name={icon} size={13} />
      {label}
    </>
  );
  return onRemove ? (
    <button
      type="button"
      className="ws-context-chip"
      disabled={disabled}
      aria-label={uiText('Remove attachment: {value1}', { value1: label })}
      onClick={onRemove}
    >
      {content}
      <WorkspaceIcon name="close" size={12} />
    </button>
  ) : (
    <span
      className="ws-context-chip"
      title={uiText('Saved in this Skill. Edit the Skill to change its references.')}
    >
      {content}
    </span>
  );
}

export type ContextReferenceKey = 'fileIds' | 'sourceIds' | 'resultIds';
const CONTEXT_FIELDS: { key: ContextReferenceKey; icon: WorkspaceIconName }[] = [
  { key: 'fileIds', icon: 'file' },
  { key: 'sourceIds', icon: 'knowledge' },
  { key: 'resultIds', icon: 'results' },
];
/** The name a reference chip shows (the composer's chip and the sent message's chip say the same thing). */
export function contextReferenceLabel(
  ws: WorkspaceState,
  key: ContextReferenceKey,
  id: string,
  uiText: WorkspaceText = workspaceTextFor('en'),
) {
  if (key === 'fileIds')
    return (
      ws.contextReferences?.files.find((item) => item.id === id)?.name ??
      ws.data.files.find((item) => item.id === id)?.name ??
      uiText('Company file')
    );
  if (key === 'sourceIds')
    return (
      ws.contextReferences?.sources.find((item) => item.id === id)?.title ??
      ws.data.sources.find((item) => item.id === id)?.title ??
      uiText('Source')
    );
  return (
    ws.contextReferences?.results.find((item) => item.id === id)?.title ??
    ws.data.results.find((item) => item.id === id)?.title ??
    uiText('Result')
  );
}

export function WorkspaceContextChips({ ws, disabled }: { ws: WorkspaceState; disabled: boolean }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const context = ws.turnContext;
  const skill = ws.data.skills.find((item) => item.id === context.intent.skillId);
  const snapshot = selectedSkillSnapshot(context, skill);
  const change = ws.setTurnContext;
  const apps = useNamespaceCopy(appsNavCopy);
  // A new App is ordinary chat with the App panel waiting (apps/new-app.ts); the chip closes that panel.
  const newApp = context.intent.mode === 'chat' && isNewAppCanvas(ws.canvas);
  // A new agent's chat (agents/new-agent.ts); the chip ends that, and closes its waiting canvas.
  const newAgent = makingNewAgent(context);
  const buildCopy = useNamespaceCopy(buildWatchCopy);
  const storefrontText = useNamespaceCopy(storefrontCopy);
  // A part of the shopping assistant: its own text, else "a part of the assistant" in the reader's language.
  const storefrontChip = context.storefrontSelection?.text
    ? appSelectionLabel(
        { tag: context.storefrontSelection.tag, text: context.storefrontSelection.text },
        buildCopy,
      )
    : storefrontText.selectedAssistantPart;
  const fields = CONTEXT_FIELDS.map((field) => ({
    ...field,
    label: (id: string) => contextReferenceLabel(ws, field.key, id, uiText),
  }));
  const attached = fields.some(({ key }) => context[key].length > 0);
  if (
    !attached &&
    context.intent.mode === 'chat' &&
    !context.selection &&
    !context.appSelection &&
    !context.storefrontSelection &&
    !context.storefrontConversations &&
    !newApp &&
    !newAgent &&
    !hasProjectChips(ws)
  )
    return null;
  return (
    <div className="ws-context-chips">
      {ws.contextReferencesError && (
        <span role="status" className="ws-muted">
          {ws.contextReferencesError}
        </span>
      )}
      <ProjectContextChips ws={ws} disabled={disabled} />
      {context.intent.mode !== 'chat' && (
        <button
          type="button"
          className="ws-context-chip"
          disabled={disabled}
          title={uiText('Remove Skill context')}
          onClick={() => change({ ...context, intent: { mode: 'chat' }, selection: undefined })}
        >
          <WorkspaceIcon name="skills" size={13} />
          {context.intent.mode === 'skill-author'
            ? uiText('Refining')
            : context.intent.mode === 'skill-preview'
              ? uiText('Previewing')
              : uiText('Using')}{' '}
          {snapshot?.name ??
            (context.intent.skillId && context.intent.revision !== undefined
              ? uiText('Skill revision {value1}', { value1: context.intent.revision })
              : uiText('a new Skill'))}
          <WorkspaceIcon name="close" size={12} />
        </button>
      )}
      {newApp && (
        <button
          type="button"
          className="ws-context-chip"
          disabled={disabled}
          title={apps.appsNewChipRemove}
          onClick={() => ws.openCanvas(null)}
        >
          <WorkspaceIcon name="app" size={13} />
          {apps.appsNewChip}
          <WorkspaceIcon name="close" size={12} />
        </button>
      )}
      {newAgent && (
        <button
          type="button"
          className="ws-context-chip"
          disabled={disabled}
          title={uiText('Stop making a new agent in this chat')}
          onClick={() => {
            change(withoutNewAgent(context));
            if (isNewAgentCanvas(ws.canvas)) ws.openCanvas(null);
          }}
        >
          {uiRich('{value1} Making a new agent {value2}', {
            value1: <WorkspaceIcon name="agents" size={13} />,
            value2: <WorkspaceIcon name="close" size={12} />,
          })}
        </button>
      )}
      {snapshot && (snapshot.fileIds.length > 0 || snapshot.sourceIds.length > 0) && (
        <details aria-label={uiText('Saved Skill references')}>
          <summary className="ws-context-chip">
            {uiRich('Included with Skill · {files} · {sources} {icon}', {
              files: uiText(snapshot.fileIds.length === 1 ? '{count} file' : '{count} files', {
                count: snapshot.fileIds.length,
              }),
              sources: uiText(
                snapshot.sourceIds.length === 1 ? '{count} source' : '{count} sources',
                { count: snapshot.sourceIds.length },
              ),
              icon: <WorkspaceIcon name="down" size={12} />,
            })}
          </summary>
          <div className="ws-context-chips">
            {fields
              .filter(({ key }) => key !== 'resultIds')
              .flatMap(({ key, icon, label }) =>
                snapshot[key as 'fileIds' | 'sourceIds'].map((id) => (
                  <ReferenceChip
                    key={`${key}:${id}`}
                    label={label(id)}
                    icon={icon}
                    disabled={disabled}
                  />
                )),
              )}
          </div>
        </details>
      )}
      {context.intent.skillId && !snapshot && (
        <span className="ws-muted" role="status">
          {uiText(
            'Selected Skill revision is unavailable. Select the Skill again to view its saved references.',
          )}
        </span>
      )}
      {attached && (
        <div
          className="ws-context-chips"
          role="group"
          aria-label={uiText('Conversation attachments')}
        >
          {context.intent.mode !== 'chat' && (
            <span className="ws-muted">{uiText('Added for this conversation')}</span>
          )}
          {fields.flatMap(({ key, icon, label }) =>
            context[key].map((id) => (
              <ReferenceChip
                key={`${key}:${id}`}
                label={label(id)}
                icon={icon}
                disabled={disabled}
                onRemove={() =>
                  change({ ...context, [key]: context[key].filter((value) => value !== id) })
                }
              />
            )),
          )}
        </div>
      )}
      {context.selection && (
        <button
          type="button"
          className="ws-context-chip"
          disabled={disabled}
          onClick={() => change({ ...context, selection: undefined })}
        >
          {uiRich('Selected passage {value1}', {
            value1: <WorkspaceIcon name="close" size={12} />,
          })}
        </button>
      )}
      {context.appSelection && (
        <button
          type="button"
          className="ws-context-chip"
          data-testid="app-selection-chip"
          disabled={disabled}
          title={buildCopy.removeSelection}
          aria-label={`${appSelectionLabel(context.appSelection, buildCopy)}. ${buildCopy.removeSelection}`}
          onClick={() => change({ ...context, appSelection: undefined })}
        >
          <WorkspaceIcon name="pointer" size={13} />
          {appSelectionLabel(context.appSelection, buildCopy)}
          <WorkspaceIcon name="close" size={12} />
        </button>
      )}
      {context.storefrontSelection && (
        <button
          type="button"
          className="ws-context-chip"
          data-testid="storefront-selection-chip"
          disabled={disabled}
          title={buildCopy.removeSelection}
          aria-label={`${storefrontChip}. ${buildCopy.removeSelection}`}
          onClick={() => change({ ...context, storefrontSelection: undefined })}
        >
          <WorkspaceIcon name="pointer" size={13} />
          {storefrontChip}
          <WorkspaceIcon name="close" size={12} />
        </button>
      )}
      {context.storefrontConversations && (
        <button
          type="button"
          className="ws-context-chip"
          data-testid="storefront-conversations-chip"
          disabled={disabled}
          title={storefrontText.removeConversations}
          aria-label={`${context.storefrontConversations.label}. ${storefrontText.removeConversations}`}
          onClick={() => change({ ...context, storefrontConversations: undefined })}
        >
          <WorkspaceIcon name="chat" size={13} />
          {context.storefrontConversations.label}
          <WorkspaceIcon name="close" size={12} />
        </button>
      )}
    </div>
  );
}
